import { useState, useCallback, useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

// Types for the wizard
export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6;

export type CourseLevel = "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
export type CourseDuration = "SHORT" | "MEDIUM" | "LONG";

export interface WizardChapter {
  title: string;
  description: string;
  estimatedDuration: number;
  includeQuiz: boolean;
}

export interface GeneratedChapter extends WizardChapter {
  id?: string;
  sections?: Array<{
    title: string;
    content: string;
  }>;
  quiz?: {
    title: string;
    questions: Array<{
      text: string;
      explanation: string;
      options: Array<{
        text: string;
        isCorrect: boolean;
      }>;
    }>;
  };
  status: "pending" | "generating" | "completed" | "error";
  errorMessage?: string;
}

export interface WizardState {
  // Current step
  currentStep: WizardStep;

  // Step 1: Basic configuration
  topic: string;
  level: CourseLevel;
  duration: CourseDuration;
  additionalNotes: string;

  // Step 2: Reference documents
  referenceFiles: Array<{
    id: string;
    name: string;
    type: string;
    url: string;
  }>;
  referenceText: string;

  // Step 3: Course structure (outline)
  courseTitle: string;
  courseDescription: string;
  shortDescription: string;
  chapters: WizardChapter[];
  isOutlineGenerated: boolean;
  isGeneratingOutline: boolean;

  // Step 4: Content generation
  generatedChapters: GeneratedChapter[];
  generationProgress: number;
  isGeneratingContent: boolean;
  currentGeneratingChapter: number | null;

  // Job tracking (para resume)
  currentJobId: string | null;
  canResumeJob: boolean;
  lastCompletedChapterIndex: number;

  // Step 5: Review state
  // (uses generatedChapters)

  // Step 6: Publish configuration
  isFree: boolean;
  price: number | null;
  currency: "USD" | "CRC";
  categoryIds: string[];
  thumbnailUrl: string | null;
  courseId: string | null;

  // Actions
  setStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1 actions
  setTopic: (topic: string) => void;
  setLevel: (level: CourseLevel) => void;
  setDuration: (duration: CourseDuration) => void;
  setAdditionalNotes: (notes: string) => void;

  // Step 2 actions
  addReferenceFile: (file: WizardState["referenceFiles"][0]) => void;
  removeReferenceFile: (id: string) => void;
  setReferenceText: (text: string) => void;

  // Step 3 actions
  setCourseTitle: (title: string) => void;
  setCourseDescription: (description: string) => void;
  setShortDescription: (description: string) => void;
  setChapters: (chapters: WizardChapter[]) => void;
  addChapter: (chapter: WizardChapter) => void;
  updateChapter: (index: number, chapter: Partial<WizardChapter>) => void;
  removeChapter: (index: number) => void;
  reorderChapters: (fromIndex: number, toIndex: number) => void;
  setIsOutlineGenerated: (value: boolean) => void;
  setIsGeneratingOutline: (value: boolean) => void;

  // Step 4 actions
  setGeneratedChapters: (chapters: GeneratedChapter[]) => void;
  updateGeneratedChapter: (
    index: number,
    chapter: Partial<GeneratedChapter>,
  ) => void;
  setGenerationProgress: (progress: number) => void;
  setIsGeneratingContent: (value: boolean) => void;
  setCurrentGeneratingChapter: (index: number | null) => void;

  // Job tracking actions
  setCurrentJobId: (jobId: string | null) => void;
  setCanResumeJob: (canResume: boolean) => void;
  setLastCompletedChapterIndex: (index: number) => void;
  clearJobState: () => void;

  // Step 6 actions
  setIsFree: (isFree: boolean) => void;
  setPrice: (price: number | null) => void;
  setCurrency: (currency: "USD" | "CRC") => void;
  setCategoryIds: (ids: string[]) => void;
  setThumbnailUrl: (url: string | null) => void;
  setCourseId: (id: string | null) => void;

  // Global actions
  reset: () => void;
  canProceed: () => boolean;
}

const initialState = {
  currentStep: 1 as WizardStep,
  topic: "",
  level: "BEGINNER" as CourseLevel,
  duration: "MEDIUM" as CourseDuration,
  additionalNotes: "",
  referenceFiles: [],
  referenceText: "",
  courseTitle: "",
  courseDescription: "",
  shortDescription: "",
  chapters: [],
  isOutlineGenerated: false,
  isGeneratingOutline: false,
  generatedChapters: [],
  generationProgress: 0,
  isGeneratingContent: false,
  currentGeneratingChapter: null,
  currentJobId: null,
  canResumeJob: false,
  lastCompletedChapterIndex: -1,
  isFree: true,
  price: null,
  currency: "USD" as const,
  categoryIds: [],
  thumbnailUrl: null,
  courseId: null,
};

export const useCourseWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Navigation
      setStep: (step) => set({ currentStep: step }),
      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 6) {
          set({ currentStep: (currentStep + 1) as WizardStep });
        }
      },
      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: (currentStep - 1) as WizardStep });
        }
      },

      // Step 1 actions
      setTopic: (topic) => set({ topic }),
      setLevel: (level) => set({ level }),
      setDuration: (duration) => set({ duration }),
      setAdditionalNotes: (additionalNotes) => set({ additionalNotes }),

      // Step 2 actions
      addReferenceFile: (file) =>
        set((state) => ({
          referenceFiles: [...state.referenceFiles, file],
        })),
      removeReferenceFile: (id) =>
        set((state) => ({
          referenceFiles: state.referenceFiles.filter((f) => f.id !== id),
        })),
      setReferenceText: (referenceText) => set({ referenceText }),

      // Step 3 actions
      setCourseTitle: (courseTitle) => set({ courseTitle }),
      setCourseDescription: (courseDescription) => set({ courseDescription }),
      setShortDescription: (shortDescription) => set({ shortDescription }),
      setChapters: (chapters) => set({ chapters }),
      addChapter: (chapter) =>
        set((state) => ({
          chapters: [...state.chapters, chapter],
        })),
      updateChapter: (index, chapter) =>
        set((state) => ({
          chapters: state.chapters.map((c, i) =>
            i === index ? { ...c, ...chapter } : c,
          ),
        })),
      removeChapter: (index) =>
        set((state) => ({
          chapters: state.chapters.filter((_, i) => i !== index),
        })),
      reorderChapters: (fromIndex, toIndex) =>
        set((state) => {
          const newChapters = [...state.chapters];
          const [removed] = newChapters.splice(fromIndex, 1);
          if (removed) {
            newChapters.splice(toIndex, 0, removed);
          }
          return { chapters: newChapters };
        }),
      setIsOutlineGenerated: (isOutlineGenerated) =>
        set({ isOutlineGenerated }),
      setIsGeneratingOutline: (isGeneratingOutline) =>
        set({ isGeneratingOutline }),

      // Step 4 actions
      setGeneratedChapters: (generatedChapters) => set({ generatedChapters }),
      updateGeneratedChapter: (index, chapter) =>
        set((state) => ({
          generatedChapters: state.generatedChapters.map((c, i) =>
            i === index ? { ...c, ...chapter } : c,
          ),
        })),
      setGenerationProgress: (generationProgress) =>
        set({ generationProgress }),
      setIsGeneratingContent: (isGeneratingContent) =>
        set({ isGeneratingContent }),
      setCurrentGeneratingChapter: (currentGeneratingChapter) =>
        set({ currentGeneratingChapter }),

      // Job tracking actions
      setCurrentJobId: (currentJobId) => set({ currentJobId }),
      setCanResumeJob: (canResumeJob) => set({ canResumeJob }),
      setLastCompletedChapterIndex: (lastCompletedChapterIndex) =>
        set({ lastCompletedChapterIndex }),
      clearJobState: () =>
        set({
          currentJobId: null,
          canResumeJob: false,
          lastCompletedChapterIndex: -1,
          generationProgress: 0,
          isGeneratingContent: false,
          currentGeneratingChapter: null,
        }),

      // Step 6 actions
      setIsFree: (isFree) =>
        set({ isFree, price: isFree ? null : get().price }),
      setPrice: (price) => set({ price }),
      setCurrency: (currency) => set({ currency }),
      setCategoryIds: (categoryIds) => set({ categoryIds }),
      setThumbnailUrl: (thumbnailUrl) => set({ thumbnailUrl }),
      setCourseId: (courseId) => set({ courseId }),

      // Global actions
      reset: () => set(initialState),

      canProceed: () => {
        const state = get();
        switch (state.currentStep) {
          case 1:
            return state.topic.trim().length >= 3;
          case 2:
            // Step 2 is optional, always can proceed
            return true;
          case 3:
            return (
              state.isOutlineGenerated &&
              state.chapters.length > 0 &&
              state.courseTitle.trim().length > 0
            );
          case 4:
            return (
              state.generatedChapters.length > 0 &&
              state.generatedChapters.every((c) => c.status === "completed")
            );
          case 5:
            // Review step, can always proceed if content exists
            return state.generatedChapters.length > 0;
          case 6:
            // Final step
            return (
              (state.isFree || (state.price !== null && state.price > 0)) &&
              state.courseId !== null
            );
          default:
            return false;
        }
      },
    }),
    {
      name: "course-wizard-storage",
      partialize: (state) => ({
        // Only persist essential data, not generation states
        currentStep: state.currentStep,
        topic: state.topic,
        level: state.level,
        duration: state.duration,
        additionalNotes: state.additionalNotes,
        referenceText: state.referenceText,
        courseTitle: state.courseTitle,
        courseDescription: state.courseDescription,
        shortDescription: state.shortDescription,
        chapters: state.chapters,
        isOutlineGenerated: state.isOutlineGenerated,
        isFree: state.isFree,
        price: state.price,
        currency: state.currency,
        categoryIds: state.categoryIds,
        // Persistir chapters generados con sections y quiz
        generatedChapters: state.generatedChapters,
        // Persistir estado del job para resume entre sesiones
        currentJobId: state.currentJobId,
        canResumeJob: state.canResumeJob,
        lastCompletedChapterIndex: state.lastCompletedChapterIndex,
        generationProgress: state.generationProgress,
      }),
    },
  ),
);

// Computed values hook
export function useCourseWizard() {
  const store = useCourseWizardStore();

  const stepLabels = useMemo(
    () => ({
      1: "Configuracion",
      2: "Referencias",
      3: "Estructura",
      4: "Generacion",
      5: "Revision",
      6: "Publicacion",
    }),
    [],
  );

  const stepDescriptions = useMemo(
    () => ({
      1: "Define el tema, nivel y duracion del curso",
      2: "Agrega documentos de referencia (opcional)",
      3: "Revisa y ajusta la estructura del curso",
      4: "Genera el contenido con AI",
      5: "Revisa y ajusta el contenido generado",
      6: "Configura precio y publica",
    }),
    [],
  );

  const durationLabels = useMemo(
    () => ({
      SHORT: "Corto (1-2 horas)",
      MEDIUM: "Medio (3-5 horas)",
      LONG: "Largo (6+ horas)",
    }),
    [],
  );

  const levelLabels = useMemo(
    () => ({
      BEGINNER: "Principiante",
      INTERMEDIATE: "Intermedio",
      ADVANCED: "Avanzado",
    }),
    [],
  );

  const estimatedChapters = useMemo(() => {
    switch (store.duration) {
      case "SHORT":
        return { min: 3, max: 5 };
      case "MEDIUM":
        return { min: 5, max: 8 };
      case "LONG":
        return { min: 8, max: 12 };
    }
  }, [store.duration]);

  const completionPercentage = useMemo(() => {
    const stepWeight = 100 / 6;
    const baseCompletion = (store.currentStep - 1) * stepWeight;

    // Add partial completion for current step
    let currentStepProgress = 0;
    switch (store.currentStep) {
      case 1:
        if (store.topic) currentStepProgress = 0.5;
        break;
      case 3:
        if (store.isOutlineGenerated) currentStepProgress = 1;
        else if (store.isGeneratingOutline) currentStepProgress = 0.5;
        break;
      case 4:
        currentStepProgress = store.generationProgress / 100;
        break;
    }

    return Math.round(baseCompletion + currentStepProgress * stepWeight);
  }, [
    store.currentStep,
    store.topic,
    store.isOutlineGenerated,
    store.isGeneratingOutline,
    store.generationProgress,
  ]);

  return {
    ...store,
    stepLabels,
    stepDescriptions,
    durationLabels,
    levelLabels,
    estimatedChapters,
    completionPercentage,
  };
}
